import { useState, useCallback } from 'react'
import { api, ApiError, getDeviceId } from '../utils/api'
import logger from '../utils/logger'
import { toast } from 'react-hot-toast'
import { trackEditModeToggled, trackPollTitleEdited, trackChoiceTextEdited, trackChoiceDeleted, trackErrorTitleEdit, trackErrorChoiceEdit, trackErrorChoiceDelete } from '../utils/analytics'
import useAuth from './useAuth'

/**
 * Custom hook for managing edit mode and edit operations
 * @param {string} pollId - The poll ID
 * @param {object} poll - The poll object with creator_device_id and user_id
 * @param {Array} choices - Current choices array
 * @param {Function} setChoices - Function to update choices
 * @param {Function} setPoll - Function to update poll
 * @returns {Object} Hook state and functions
 */
function useEditMode(pollId, poll, choices, setChoices, setPoll) {
    const [editMode, setEditMode] = useState(false)
    const [editingChoiceId, setEditingChoiceId] = useState(null)
    const [editingChoiceText, setEditingChoiceText] = useState('')
    const [editingPollTitle, setEditingPollTitle] = useState(false)
    const [editingTitleText, setEditingTitleText] = useState('')
    const [isUpdating, setIsUpdating] = useState(false)

    const deviceId = getDeviceId()
    const { user } = useAuth()
    const userId = user?.id || null

    // Permission checks - check user_id first (for logged-in users), then fall back to device_id
    const isPollCreator =
        (userId && poll?.user_id && poll.user_id === userId) ||
        (poll?.creator_device_id && poll.creator_device_id === deviceId)

    const canEditPoll = isPollCreator

    const canEditChoice = useCallback((choice) => {
        if (!choice) return false
        // Poll creator can edit any choice
        if (isPollCreator) return true
        // Check user_id first (for authenticated users)
        if (userId && choice.user_id && choice.user_id === userId) return true
        // Fall back to device_id (for anonymous users)
        return choice.creator_device_id && choice.creator_device_id === deviceId
    }, [isPollCreator, userId, deviceId])

    // Check if user has any editable choices (for showing edit toggle to non-creators)
    const hasEditableChoices = choices?.some(choice =>
        (userId && choice.user_id && choice.user_id === userId) ||
        (choice.creator_device_id && choice.creator_device_id === deviceId)
    ) || false

    // Toggle edit mode
    const toggleEditMode = useCallback(() => {
        setEditMode(prev => {
            const newMode = !prev
            trackEditModeToggled(pollId, newMode)
            if (!newMode) {
                // Exiting edit mode, clear any editing state
                setEditingChoiceId(null)
                setEditingChoiceText('')
                setEditingPollTitle(false)
                setEditingTitleText('')
            }
            return newMode
        })
    }, [pollId])

    // Start editing poll title
    const startEditingPollTitle = useCallback(() => {
        if (!canEditPoll) return
        setEditingPollTitle(true)
        setEditingTitleText(poll?.title || '')
    }, [canEditPoll, poll?.title])

    // Cancel editing poll title
    const cancelEditingPollTitle = useCallback(() => {
        setEditingPollTitle(false)
        setEditingTitleText('')
    }, [])

    // Save poll title
    const savePollTitle = useCallback(async () => {
        if (!canEditPoll || !editingTitleText.trim()) {
            toast.error('Title cannot be empty')
            return
        }

        // Skip API call if text hasn't changed
        if (editingTitleText.trim() === poll?.title) {
            logger.info('Poll title unchanged, skipping update', { pollId })
            setEditingPollTitle(false)
            setEditingTitleText('')
            return
        }

        setIsUpdating(true)
        try {
            logger.info('Updating poll title', { pollId, newTitle: editingTitleText.substring(0, 50) })

            const response = await api.patch(`/api/polls/${pollId}`, {
                title: editingTitleText.trim(),
                device_id: deviceId
            })

            // Update local state
            if (setPoll && typeof setPoll === 'function') {
                setPoll(prev => ({ ...prev, title: response.title }))
            }

            setEditingPollTitle(false)
            setEditingTitleText('')
            toast.success('Poll title updated!')
            trackPollTitleEdited(pollId)
            logger.info('Poll title updated successfully', { pollId })
        } catch (err) {
            const errorMessage = err instanceof ApiError
                ? err.message
                : 'Failed to update poll title'
            logger.error('Failed to update poll title', { pollId, error: errorMessage })
            trackErrorTitleEdit(pollId, errorMessage)
            toast.error(errorMessage)
        } finally {
            setIsUpdating(false)
        }
    }, [canEditPoll, editingTitleText, pollId, deviceId, setPoll, poll])

    // Start editing a choice
    const startEditingChoice = useCallback((choice) => {
        if (!canEditChoice(choice)) return
        setEditingChoiceId(choice.id)
        setEditingChoiceText(choice.text)
    }, [canEditChoice])

    // Cancel editing choice
    const cancelEditingChoice = useCallback(() => {
        setEditingChoiceId(null)
        setEditingChoiceText('')
    }, [])

    // Save choice text
    const saveChoiceText = useCallback(async () => {
        if (!editingChoiceId || !editingChoiceText.trim()) {
            toast.error('Choice text cannot be empty')
            return
        }

        // Skip API call if text hasn't changed
        const originalChoice = choices.find(c => c.id === editingChoiceId)
        if (originalChoice && editingChoiceText.trim() === originalChoice.text) {
            logger.info('Choice text unchanged, skipping update', { choiceId: editingChoiceId })
            setEditingChoiceId(null)
            setEditingChoiceText('')
            return
        }

        setIsUpdating(true)
        try {
            logger.info('Updating choice text', { choiceId: editingChoiceId, newText: editingChoiceText.substring(0, 50) })

            const response = await api.patch(`/api/choices/${editingChoiceId}`, {
                text: editingChoiceText.trim(),
                device_id: deviceId
            })

            // Update local state
            setChoices(prev => prev.map(choice =>
                choice.id === editingChoiceId
                    ? { ...choice, text: response.text }
                    : choice
            ))

            setEditingChoiceId(null)
            setEditingChoiceText('')
            toast.success('Choice updated!')
            trackChoiceTextEdited(pollId, editingChoiceId)
            logger.info('Choice updated successfully', { choiceId: editingChoiceId })
        } catch (err) {
            const errorMessage = err instanceof ApiError
                ? err.message
                : 'Failed to update choice'
            logger.error('Failed to update choice', { choiceId: editingChoiceId, error: errorMessage })
            trackErrorChoiceEdit(pollId, editingChoiceId, errorMessage)
            toast.error(errorMessage)
        } finally {
            setIsUpdating(false)
        }
    }, [editingChoiceId, editingChoiceText, deviceId, setChoices, choices, pollId])

    // Delete a choice
    const deleteChoice = useCallback(async (choiceId) => {
        const choice = choices.find(c => c.id === choiceId)
        if (!choice || !canEditChoice(choice)) {
            toast.error('You cannot delete this choice')
            return
        }

        setIsUpdating(true)
        try {
            logger.info('Deleting choice', { choiceId })

            await api.delete(`/api/choices/${choiceId}`, {
                device_id: deviceId
            })

            // Update local state
            setChoices(prev => prev.filter(c => c.id !== choiceId))

            toast.success('Choice deleted!')
            trackChoiceDeleted(pollId, choiceId)
            logger.info('Choice deleted successfully', { choiceId })
        } catch (err) {
            const errorMessage = err instanceof ApiError
                ? err.message
                : 'Failed to delete choice'
            logger.error('Failed to delete choice', { choiceId, error: errorMessage })
            trackErrorChoiceDelete(pollId, choiceId, errorMessage)
            toast.error(errorMessage)
        } finally {
            setIsUpdating(false)
        }
    }, [choices, canEditChoice, deviceId, setChoices, pollId])

    return {
        // State
        editMode,
        editingChoiceId,
        editingChoiceText,
        setEditingChoiceText,
        editingPollTitle,
        editingTitleText,
        setEditingTitleText,
        isUpdating,

        // Permissions
        isPollCreator,
        canEditPoll,
        canEditChoice,
        hasEditableChoices,
        deviceId,

        // Actions
        toggleEditMode,
        startEditingPollTitle,
        cancelEditingPollTitle,
        savePollTitle,
        startEditingChoice,
        cancelEditingChoice,
        saveChoiceText,
        deleteChoice
    }
}

export default useEditMode
